import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError, getJson, postJson } from '@/api/client'
import type {
  BotSimulationBatch,
  BotSimulationBatchDetail,
  BotSimulationItemDetail,
  BotSimulationItemSummary,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { AppShell } from '@/ui/AppShell'
import { Button } from '@/ui/Button'
import { EmptyState } from '@/ui/EmptyState'
import { Modal } from '@/ui/Modal'
import { Badge, SectionTitle } from '@/ui/primitives'
import { toast } from '@/ui/Toast'

const statusVariant = (status: string): 'default' | 'success' | 'warning' | 'danger' => {
  if (status === 'completed') return 'success'
  if (status === 'running') return 'warning'
  if (status === 'failed') return 'danger'
  return 'default'
}

const formatTs = (value: string | null) => (value ? new Date(value).toLocaleString() : '-')
const formatMoney = (value: number | null | undefined) => `$${Number(value || 0).toFixed(4)}`
const errorMessage = (err: unknown) => {
  if (err instanceof ApiError && err.detail) return err.detail
  if (err instanceof Error) return err.message
  return String(err)
}

interface ContextDiscoveryDebug {
  source?: string
  fallback_reason?: string
  classifier_model?: string
  intent?: string
  active_question?: string
  resolution_status?: string
  context_summary?: string
  answer_guidance?: string
  needs_vision?: boolean
  discovered_message_count?: number
  total_examined_message_count?: number
  expansion_count?: number
  current_window_image_attempted_count?: number
  current_window_image_downloaded_count?: number
  historical_image_attempted_count?: number
  historical_image_downloaded_count?: number
}

function contextStatusLabel(context: ContextDiscoveryDebug | null): string {
  if (!context) return 'Not recorded'
  if (context.source === 'gemini') return 'Gemini classified'
  if (context.source === 'no_history' || context.fallback_reason === 'no_messages_available') return 'No prior history'
  if (context.source === 'fallback') return 'Fallback used'
  return String(context.source || 'Unknown')
}

function contextStatusVariant(context: ContextDiscoveryDebug | null): 'default' | 'success' | 'warning' | 'danger' {
  if (!context) return 'default'
  if (context.source === 'gemini') return 'success'
  if (context.source === 'fallback') return 'warning'
  return 'default'
}

function contextSummaryText(context: ContextDiscoveryDebug): string {
  if (context.source === 'no_history' || context.fallback_reason === 'no_messages_available') {
    return 'No prior customer-agent messages were available before this simulation window, so Gemini context discovery was not called.'
  }
  return context.context_summary || '-'
}

function contextGuidanceText(context: ContextDiscoveryDebug): string {
  if (context.source === 'no_history' || context.fallback_reason === 'no_messages_available') {
    return 'Answer from the current customer window and attachments only. Ask a targeted clarification if the message depends on missing prior context.'
  }
  return context.answer_guidance || '-'
}

export function BotSimulationPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [batches, setBatches] = useState<BotSimulationBatch[]>([])
  const [detail, setDetail] = useState<BotSimulationBatchDetail | null>(null)
  const [selectedItem, setSelectedItem] = useState<BotSimulationItemDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [limit, setLimit] = useState(100)
  const [concurrency, setConcurrency] = useState(2)

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/', { replace: true })
      return
    }
    void loadBatches()
  }, [user, navigate])

  useEffect(() => {
    if (!detail || detail.batch.status !== 'running') return
    const id = window.setInterval(() => {
      void loadBatch(detail.batch.id, false)
    }, 3000)
    return () => window.clearInterval(id)
  }, [detail?.batch.id, detail?.batch.status])

  async function loadBatches() {
    setLoading(true)
    setError(null)
    try {
      const rows = await getJson<BotSimulationBatch[]>('/v1/admin/bot-simulations/batches')
      setBatches(rows)
      if (!detail && rows[0]) {
        await loadBatch(rows[0].id, false)
      }
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function loadBatch(batchId: number, loud = true) {
    if (loud) setLoading(true)
    setError(null)
    try {
      const row = await getJson<BotSimulationBatchDetail>(`/v1/admin/bot-simulations/batches/${batchId}?limit=200`)
      setDetail(row)
      setBatches((current) => current.map((batch) => (batch.id === row.batch.id ? row.batch : batch)))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      if (loud) setLoading(false)
    }
  }

  async function createBatch() {
    setBusy(true)
    setError(null)
    try {
      const batch = await postJson<BotSimulationBatch>('/v1/admin/bot-simulations/batches', {
        buffer_seconds: 60,
      })
      setBatches((rows) => [batch, ...rows])
      await loadBatch(batch.id, false)
      toast.success('Batch created')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function startBatch() {
    if (!detail) return
    setBusy(true)
    setError(null)
    try {
      const batch = await postJson<BotSimulationBatch>(
        `/v1/admin/bot-simulations/batches/${detail.batch.id}/start`,
        { limit, concurrency },
      )
      setDetail((current) => current ? { ...current, batch } : current)
      setBatches((rows) => rows.map((row) => (row.id === batch.id ? batch : row)))
      toast.success('Batch processing started')
      window.setTimeout(() => void loadBatch(batch.id, false), 1200)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function pauseBatch() {
    if (!detail) return
    setBusy(true)
    setError(null)
    try {
      const batch = await postJson<BotSimulationBatch>(
        `/v1/admin/bot-simulations/batches/${detail.batch.id}/pause`,
        {},
      )
      setDetail((current) => current ? { ...current, batch } : current)
      setBatches((rows) => rows.map((row) => (row.id === batch.id ? batch : row)))
      toast.success('Batch paused')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function openItem(item: BotSimulationItemSummary) {
    setBusy(true)
    setError(null)
    try {
      const row = await getJson<BotSimulationItemDetail>(`/v1/admin/bot-simulations/items/${item.id}`)
      setSelectedItem(row)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const progress = useMemo(() => {
    if (!detail || detail.batch.total_items === 0) return 0
    return Math.round(((detail.batch.completed_items + detail.batch.failed_items) / detail.batch.total_items) * 100)
  }, [detail])

  const selectedBatchId = detail?.batch.id
  const selectedContext = selectedItem?.context_discovery as ContextDiscoveryDebug | null | undefined

  return (
    <AppShell>
      <div className="w-full px-6 lg:px-10 2xl:px-12 py-10 h-full overflow-y-auto">
        <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-3 text-xs text-mute mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-ember" />
              <button className="hover:text-cream transition-colors" onClick={() => navigate('/admin')} type="button">Admin</button>
              <span>/</span>
              <span>Bot Simulations</span>
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-medium text-cream tracking-tight leading-[1.05]">
              Batch Bot Simulation
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="ghost" onClick={() => void loadBatches()} disabled={loading}>Refresh</Button>
            <Button size="sm" onClick={createBatch} loading={busy}>Create last 24h batch</Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 text-sm border-l-[3px] border-ember text-ember pl-4 py-2 bg-ember/5 rounded-r-lg">
            {error}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)] 2xl:grid-cols-[400px_minmax(0,1fr)]">
          <section className="border border-haze/40 bg-ink-raised rounded-lg shadow-elevated overflow-hidden">
            <div className="px-5 py-4 border-b border-haze/40 flex items-center justify-between">
              <SectionTitle>Batches</SectionTitle>
              <Badge variant="default">{batches.length}</Badge>
            </div>
            {batches.length === 0 ? (
              <EmptyState icon="◦" title="No batches" description="Create a local replay batch from the last 24 hours." />
            ) : (
              <div className="divide-y divide-haze/30 max-h-[680px] overflow-y-auto">
                {batches.map((batch) => (
                  <button
                    key={batch.id}
                    onClick={() => void loadBatch(batch.id)}
                    type="button"
                    className={`w-full px-5 py-4 text-left transition-colors ${
                      selectedBatchId === batch.id ? 'bg-ink border-l-[3px] border-ember' : 'hover:bg-ink/45'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="font-display text-cream">Batch #{batch.id}</div>
                      <Badge variant={statusVariant(batch.status)}>{batch.status}</Badge>
                    </div>
                    <div className="text-xs text-mute leading-relaxed">
                      {batch.total_items} windows · {batch.total_customer_messages} messages
                    </div>
                    <div className="text-xs text-mute mt-1">{formatTs(batch.created_at)}</div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="min-w-0">
            {!detail ? (
              <div className="border border-haze/40 bg-ink-raised rounded-lg shadow-elevated">
                <EmptyState icon="◦" title="Select a batch" description="Batch details and local drafts will appear here." />
              </div>
            ) : (
              <div className="space-y-6">
                <div className="border border-haze/40 bg-ink-raised rounded-lg shadow-elevated p-5">
                  <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <h2 className="font-display text-2xl text-cream">Batch #{detail.batch.id}</h2>
                        <Badge variant={statusVariant(detail.batch.status)}>{detail.batch.status}</Badge>
                      </div>
                      <div className="text-sm text-mute">
                        {formatTs(detail.batch.window_start_at)} to {formatTs(detail.batch.window_end_at)}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <label className="text-xs text-mute">
                        Limit
                        <input
                          className="mt-1 w-24 bg-ink border border-haze/50 rounded-md px-3 py-2 text-sm text-cream outline-none focus:border-ember"
                          type="number"
                          min={1}
                          max={500}
                          value={limit}
                          onChange={(event) => setLimit(Number(event.target.value))}
                        />
                      </label>
                      <label className="text-xs text-mute">
                        Concurrency
                        <input
                          className="mt-1 w-24 bg-ink border border-haze/50 rounded-md px-3 py-2 text-sm text-cream outline-none focus:border-ember"
                          type="number"
                          min={1}
                          max={5}
                          value={concurrency}
                          onChange={(event) => setConcurrency(Number(event.target.value))}
                        />
                      </label>
                      <Button size="sm" onClick={startBatch} loading={busy} disabled={detail.batch.pending_items === 0}>
                        Start
                      </Button>
                      <Button size="sm" variant="ghost" onClick={pauseBatch} disabled={busy || detail.batch.status !== 'running'}>
                        Pause
                      </Button>
                    </div>
                  </div>

                  <div className="mt-5 h-2 bg-ink rounded-full overflow-hidden">
                    <div className="h-full bg-ember transition-all" style={{ width: `${progress}%` }} />
                  </div>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    {[
                      ['Windows', detail.batch.total_items],
                      ['Pending', detail.batch.pending_items],
                      ['Done', detail.batch.completed_items],
                      ['Failed', detail.batch.failed_items],
                      ['Cost', formatMoney(detail.batch.total_cost_usd)],
                    ].map(([label, value]) => (
                      <div key={label} className="bg-ink/45 rounded-md border border-haze/30 px-4 py-3">
                        <div className="text-xs text-mute mb-1">{label}</div>
                        <div className="font-display text-xl text-cream">{value}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
                    <div className="text-mute">Customer messages: <span className="text-cream">{detail.batch.total_customer_messages}</span></div>
                    <div className="text-mute">Attachment messages: <span className="text-cream">{detail.batch.total_attachment_messages}</span></div>
                    <div className="text-mute">Images: <span className="text-cream">{detail.batch.total_image_downloaded}/{detail.batch.total_image_attempted}</span></div>
                  </div>
                </div>

                <div className="border border-haze/40 bg-ink-raised rounded-lg shadow-elevated overflow-hidden">
                  <div className="px-5 py-4 border-b border-haze/40 flex items-center justify-between">
                    <SectionTitle>Drafts</SectionTitle>
                    <span className="text-xs text-mute">Showing first 200 windows</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1360px]">
                      <thead>
                        <tr className="border-b border-haze/40 bg-ink-raised/60">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-cream">Customer</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-cream">Window</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-cream">Messages</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-cream">Images</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-cream">Status</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-cream">Draft</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-cream">Human reply</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.items.map((item) => (
                          <tr
                            key={item.id}
                            className="border-b border-haze/30 last:border-0 hover:bg-ink/35 cursor-pointer"
                            onClick={() => void openItem(item)}
                          >
                            <td className="px-4 py-3 text-sm text-cream">
                              <div>{item.customer_name || `#${item.chatapp_customer_id}`}</div>
                              <div className="text-xs text-mute">{item.chatapp_customer_id}</div>
                            </td>
                            <td className="px-4 py-3 text-xs text-mute">{formatTs(item.window_start_at)}</td>
                            <td className="px-4 py-3 text-sm text-cream">{item.message_count}</td>
                            <td className="px-4 py-3 text-sm text-cream">{item.image_downloaded_count}/{item.image_attempted_count}</td>
                            <td className="px-4 py-3"><Badge variant={statusVariant(item.status)}>{item.status}</Badge></td>
                            <td className="px-4 py-3 text-sm text-cream/85 max-w-[420px] truncate">
                              {item.generated_response || item.error || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-cream/85 max-w-[420px] truncate">
                              {item.human_response || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <Modal
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        size="full"
        className="!max-w-[min(1720px,98vw)]"
        backdropClassName="bg-ink/75"
      >
        {selectedItem && (
          <div className="p-6 lg:p-8 max-h-[82vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="text-sm text-mute mb-2">Window #{selectedItem.id}</div>
                <h3 className="font-display text-2xl text-cream">{selectedItem.customer_name || selectedItem.chatapp_customer_id}</h3>
              </div>
              <Badge variant={statusVariant(selectedItem.status)}>{selectedItem.status}</Badge>
            </div>

            <div className="grid gap-5 xl:grid-cols-3">
              <div>
                <div className="text-sm text-mute mb-2">Customer window</div>
                <pre className="whitespace-pre-wrap text-sm leading-relaxed text-cream bg-ink/60 border border-haze/30 rounded-md p-4 min-h-[180px]">
                  {selectedItem.customer_text}
                </pre>
              </div>
              <div>
                <div className="text-sm text-mute mb-2">Generated draft</div>
                <pre className="whitespace-pre-wrap text-sm leading-relaxed text-cream bg-ink/60 border border-haze/30 rounded-md p-4 min-h-[180px]">
                  {selectedItem.generated_response || selectedItem.error || '-'}
                </pre>
              </div>
              <div>
                <div className="text-sm text-mute mb-2">Human reply</div>
                <pre className="whitespace-pre-wrap text-sm leading-relaxed text-cream bg-ink/60 border border-haze/30 rounded-md p-4 min-h-[180px]">
                  {selectedItem.human_response || '-'}
                </pre>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3 text-sm">
              <div className="bg-ink/45 rounded-md border border-haze/30 p-4">
                <div className="text-mute mb-1">Images</div>
                <div className="text-cream">{selectedItem.image_downloaded_count}/{selectedItem.image_attempted_count}</div>
              </div>
              <div className="bg-ink/45 rounded-md border border-haze/30 p-4">
                <div className="text-mute mb-1">Historical images</div>
                <div className="text-cream">{selectedItem.historical_image_downloaded_count}/{selectedItem.historical_image_attempted_count}</div>
              </div>
              <div className="bg-ink/45 rounded-md border border-haze/30 p-4">
                <div className="text-mute mb-1">Cost</div>
                <div className="text-cream">{formatMoney(selectedItem.cost_usd)}</div>
              </div>
            </div>

            <div className="mt-6 bg-ink/45 rounded-md border border-haze/30 p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-mute">Context discovery</div>
                <Badge variant={contextStatusVariant(selectedContext || null)}>{contextStatusLabel(selectedContext || null)}</Badge>
              </div>
              {selectedContext ? (
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)]">
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs text-mute mb-1">Summary</div>
                      <div className="text-sm text-cream/90 leading-relaxed">
                        {contextSummaryText(selectedContext)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-mute mb-1">Guidance</div>
                      <div className="text-sm text-cream/90 leading-relaxed">
                        {contextGuidanceText(selectedContext)}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-2 text-xs text-mute">
                    <div className="flex justify-between gap-3"><span>Source</span><span className="text-cream/90 text-right">{selectedContext.source || '-'}</span></div>
                    <div className="flex justify-between gap-3"><span>Model</span><span className="text-cream/90 text-right">{selectedContext.classifier_model || '-'}</span></div>
                    <div className="flex justify-between gap-3"><span>Fallback</span><span className="text-cream/90 text-right">{selectedContext.fallback_reason || '-'}</span></div>
                    <div className="flex justify-between gap-3"><span>Intent</span><span className="text-cream/90 text-right">{selectedContext.intent || '-'}</span></div>
                    <div className="flex justify-between gap-3"><span>Status</span><span className="text-cream/90 text-right">{selectedContext.resolution_status || '-'}</span></div>
                    <div className="flex justify-between gap-3"><span>Examined</span><span className="text-cream/90 text-right">{selectedContext.total_examined_message_count ?? 0}</span></div>
                    <div className="flex justify-between gap-3"><span>Selected</span><span className="text-cream/90 text-right">{selectedContext.discovered_message_count ?? 0}</span></div>
                    <div className="flex justify-between gap-3"><span>Expansions</span><span className="text-cream/90 text-right">{selectedContext.expansion_count ?? 0}</span></div>
                    <div className="flex justify-between gap-3"><span>Current images</span><span className="text-cream/90 text-right">{selectedContext.current_window_image_downloaded_count ?? 0}/{selectedContext.current_window_image_attempted_count ?? 0}</span></div>
                    <div className="flex justify-between gap-3"><span>Historical images</span><span className="text-cream/90 text-right">{selectedContext.historical_image_downloaded_count ?? 0}/{selectedContext.historical_image_attempted_count ?? 0}</span></div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-mute">No context discovery payload was saved for this window.</div>
              )}
            </div>

            <div className="mt-6">
              <div className="text-sm text-mute mb-2">Events</div>
              <div className="space-y-2">
                {selectedItem.events.slice(-20).map((event) => (
                  <div key={event.id} className="text-xs bg-ink/45 border border-haze/30 rounded-md p-3">
                    <div className="flex items-center justify-between gap-3 text-mute mb-1">
                      <span>{event.kind}</span>
                      <span>{formatTs(event.ts)}</span>
                    </div>
                    <pre className="whitespace-pre-wrap text-cream/80 overflow-x-auto">
                      {JSON.stringify(event.payload, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </AppShell>
  )
}
